import { parse } from 'node-html-parser'
import { decode } from 'html-entities'
import { applyContainerSx } from 'common/container'

const TWO_COLUMNS = '2 columns'
const HEADING_1 = 'heading-1'
const HEADING_2 = 'heading-2'
const HEADING_3 = 'heading-3'
const HEADING_4 = 'heading-4'
const HEADING_5 = 'heading-5'
const HEADING_6 = 'heading-6'
const PARAGRAPH = 'paragraph'
const UNORDERED_LIST = 'unordered-list'
const ORDERED_LIST = 'ordered-list'
const LIST_ITEM = 'list-item'
const TABLE = 'table'
const TABLE_ROW = 'table-row'
const TABLE_HEADER_CELL = 'table-header-cell'
const TABLE_CELL = 'table-cell'
const BLOCKQUOTE = 'blockquote'
const TABLE_HEAD = 'table-head'
const TABLE_BODY = 'table-body'
const SIMPLE_GRID = 'simple-grid'
const STACK = 'stack'

const HYPERLINK = 'hyperlink'
const TEXT = 'text'
const SPAN = 'span'

const tagMapper = {
  h1: HEADING_1,
  h2: HEADING_2,
  h3: HEADING_3,
  h4: HEADING_4,
  h5: HEADING_5,
  h6: HEADING_6,
  p: PARAGRAPH,
  a: HYPERLINK,
  ul: UNORDERED_LIST,
  ol: ORDERED_LIST,
  li: LIST_ITEM,
  stack: STACK,
  simpleGrid: SIMPLE_GRID,
  table: TABLE,
  thead: TABLE_HEAD,
  tbody: TABLE_BODY,
  tr: TABLE_ROW,
  th: TABLE_HEADER_CELL,
  td: TABLE_CELL,
  blockquote: BLOCKQUOTE
}

const ITALIC_MARK = 'italic'
const BOLD_MARK = 'bold'
const markMapper = {
  em: ITALIC_MARK,
  i: ITALIC_MARK,
  strong: BOLD_MARK,
  b: BOLD_MARK
}

function extractUriFromRawAttrs(rawAttrs) {
  const pattern = /href="(.*?)"/
  const match = pattern.exec(rawAttrs?.replace('\r\n', '')?.replace('\n', '') || '')
  if (!Array.isArray(match) || !match[1]) return ''
  return match[1]
}
const isNewline = value => value === '\r\n' || value === '\n'
const filterTruthyNonEmptyValues = nodes =>
  nodes.filter(node => {
    const { value, content } = node || {}
    return content?.length > 0 || (Boolean(value) && !isNewline(value))
  })

function groupTextAndLinksIntoParagraphs(data) {
  const { content } = data
  if (content.every(({ nodeType }) => nodeType !== TEXT && nodeType !== HYPERLINK)) return data
  let openIndex = 0
  const newContent = []
  for (let index = 0; index < content.length; index++) {
    const node = content[index]
    const isInline = [TEXT, HYPERLINK].includes(node.nodeType)

    if (isInline) {
      if (openIndex < 0) {
        openIndex = index
      }
      if (index === content.length - 1) {
        newContent.push({
          nodeType: 'paragraph',
          data: {},
          content: content.slice(openIndex)
        })
      }
    } else {
      if (openIndex >= 0) {
        newContent.push({
          nodeType: 'paragraph',
          data: {},
          content: content.slice(openIndex, index)
        })
      }
      newContent.push(node)
      openIndex = index + 1
    }
  }

  return { ...data, content: newContent }
}

function processChildNodesToContentfulContent(childNodes) {
  return filterTruthyNonEmptyValues(
    childNodes.reduce((acc, curr) => {
      const processedNode = processDataToContentfulJSONFormat(curr)
      return [...acc, ...processedNode]
    }, [])
  )
}
function processDataToContentfulJSONFormat({ nodeType, childNodes, rawTagName, rawAttrs, _rawText }) {
  // an integer which specifies the type of the node: https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
  // Node.ELEMENT_NODE (1)
  if (nodeType === 1) {
    const content = processChildNodesToContentfulContent(childNodes)
    if (rawTagName === 'a') {
      return [
        {
          nodeType: HYPERLINK,
          data: { uri: extractUriFromRawAttrs(rawAttrs) },
          content
        }
      ]
    }
    if (tagMapper[rawTagName]) {
      return [
        {
          nodeType: tagMapper[rawTagName],
          data: {},
          content
        }
      ]
    }
    if (Object.keys(markMapper).includes(rawTagName)) {
      const value = childNodes.reduce((value, { _rawText }) => {
        if (_rawText?.length > 0) {
          return `${value}${_rawText}`
        }
        return value
      }, '')
      return [
        {
          nodeType: TEXT,
          marks: [
            {
              type: markMapper[rawTagName]
            }
          ],
          value: decode(value),
          data: {}
        }
      ]
    }
    if (rawTagName === SPAN) {
      return processChildNodesToContentfulContent(childNodes)
    }
  }
  // Node.TEXT_NODE (3)
  if (nodeType === 3) {
    return [
      {
        nodeType: TEXT,
        value: decode(_rawText),
        data: {},
        marks: []
      }
    ]
  }
  return []
}

export function parseSeoCategoryInfo(categoryInfo) {
  if (!(categoryInfo?.length > 0)) return

  const { childNodes } = parse(categoryInfo)
  const processed = {
    nodeType: 'document',
    data: {},
    content: processChildNodesToContentfulContent(childNodes)
  }
  return groupTextAndLinksIntoParagraphs(processed)
}

const cfMapper = data => {
  const props = {
    title: data.title,
    defaultJson: data.defaultCopy?.json,
    mobileSpecificJson: data.mobileSpecificCopy?.json,
    embedList: data.embedList,
    mobileSpecificEmbedList: data.mobileSpecificEmbedList
  }

  if (data.copyView !== TWO_COLUMNS) {
    props.desiredWidth = data.copyView
  }

  return applyContainerSx(props, data)
}

const getEmbedList = json => {
  let list = []
  if (json) {
    json.content.map(paragraph => {
      const embed = paragraph.content?.filter(
        item => item.nodeType === 'hyperlink' && item.data?.uri?.indexOf('embed:') === 0
      )
      list = [...list, ...embed]
      return false
    })
  }
  return list
}

export const parseCfSeoCategoryInfo = copy => {
  const embedList = getEmbedList(copy.defaultCopy?.json)
  const mobileSpecificEmbedList = getEmbedList(copy.mobileSpecificCopy?.json)

  return cfMapper({
    ...copy,
    embedList,
    mobileSpecificEmbedList
  })
}
